import { Hidden } from "@material-ui/core";
import { xor } from "lodash";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { useController, useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { array, BaseSchema, boolean, date, mixed, number, string } from "yup";
import { Checkbox } from "../../../components/forms/checkbox";
import { DateField } from "../../../components/forms/date";
import { FileField } from "../../../components/forms/file-3";
import { NumberField } from "../../../components/forms/number";
import { RadioGroup } from "../../../components/forms/radio";
import { Select } from "../../../components/forms/select";
import { TextField } from "../../../components/forms/text";
import { Toggle } from "../../../components/forms/toggle";
import {
  ConfirmText,
  ErrorText,
  FullWidthItem,
  ToggleWrapper,
} from "./default-fields/common";
import { Field } from "./internal/field";

export type BasicFieldRenderFuncProps = {
  item: Exclude<Field.Field<string>, Field.DefaultField>;
};

export const basicFields: Record<
  Exclude<Field.Field<string>, Field.DefaultField>["type"],
  FC<BasicFieldRenderFuncProps>
> = {
  MULTI_CHECKBOX: ({ item }) => {
    const { control } = useFormContext();
    const { field, fieldState } = useController({
      control,
      name: item.field,
    });

    const { append, remove } = useFieldArray({
      control, // control props comes from useForm (optional: if you are using FormContext)
      name: item.field, // unique name for your Field Array
    });

    const [checkedOptions, setCheckedOptions] = useState<Array<string>>([]);

    const { i18n } = useTranslation();
    const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
      i18n,
    ]);

    useEffect(() => {
      if (field.value) {
        if (xor(field.value, checkedOptions).length > 0) {
          setCheckedOptions(field.value);
        }
      }
    }, [field.value, checkedOptions]);

    const availableOptions = useMemo(() => {
      if (
        !(
          Field.isMultiCheckboxField(item) &&
          item.typeDetails.availableOptions?.[currentLanguage]
        )
      )
        return null;
      return item.typeDetails.availableOptions[currentLanguage].map(
        (option) => (
          <Checkbox
            key={option}
            checked={checkedOptions.includes(option)}
            onChange={(e) => {
              if (e.target.checked && !checkedOptions.includes(option)) {
                append(option);
              } else if (
                !e.target.checked &&
                checkedOptions.includes(option) &&
                Array.isArray(field.value)
              ) {
                remove(field.value.findIndex((o) => o === option));
              }
            }}
            label={option}
          />
        )
      );
    }, [
      checkedOptions,
      Field.isMultiCheckboxField(item) && item.typeDetails.availableOptions,
      currentLanguage,
      setCheckedOptions,
    ]);

    if (Field.isMultiCheckboxField(item)) {
      return (
        <>
          <div>{item.desc?.[currentLanguage]}</div>
          <div>
            {availableOptions}
            {fieldState.error ? (
              <ErrorText>{fieldState.error?.message}</ErrorText>
            ) : null}
          </div>
        </>
      );
    } else throw new Error("Multi Checkbox incorrectly configured");
  },
  MULTI_SELECT: ({ item }) => {
    const { control } = useFormContext();
    const { field, fieldState } = useController({
      control,
      name: item.field,
    });
    const { i18n } = useTranslation();
    const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
      i18n,
    ]);

    if (Field.isMultiSelectField(item)) {
      return (
        <>
          <div>{item.desc?.[currentLanguage]}</div>
          <Select
            {...field}
            value={field.value || []}
            multiple
            fullWidth
            label={item.title?.[currentLanguage]}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            options={
              item.typeDetails.availableOptions?.[currentLanguage]
                ? item.typeDetails.availableOptions[currentLanguage].map(
                    (value) => ({
                      title: value,
                      value,
                    })
                  )
                : []
            }
          />
        </>
      );
    } else throw new Error("Multi Select incorrectly configured");
  },
  SWITCH: ({ item }) => {
    const { control } = useFormContext();
    const { field, fieldState } = useController({
      control,
      name: item.field,
    });
    const { i18n } = useTranslation();
    const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
      i18n,
    ]);

    const innerDescription = useMemo(
      () => (
        <div>
          <div>
            <strong>{item.title?.[currentLanguage]}</strong>
          </div>
          {!!fieldState.error ? (
            <ErrorText>{fieldState.error?.message}</ErrorText>
          ) : null}
          {item.desc?.[currentLanguage]}
        </div>
      ),
      [fieldState, item, currentLanguage]
    );

    const innerToggle = useMemo(() => {
      if (Field.isSwitchField(item)) {
        return (
          <ToggleWrapper>
            <Toggle {...field} />
            <ConfirmText ticked={field.value}>
              {item.typeDetails.availableOption?.[currentLanguage] ??
                "I CONFIRM"}
            </ConfirmText>{" "}
          </ToggleWrapper>
        );
      } else throw new Error("Switch incorrectly configured");
    }, [field, item]);

    return (
      <>
        <Hidden smDown>
          {innerToggle}
          {innerDescription}
        </Hidden>
        <Hidden mdUp>
          <FullWidthItem>
            {innerToggle}
            {innerDescription}
          </FullWidthItem>
        </Hidden>
      </>
    );
  },
  DATE: ({ item }) => {
    const { control } = useFormContext();
    const { field, fieldState } = useController({
      control,
      name: item.field,
    });
    const { i18n } = useTranslation();
    const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
      i18n,
    ]);

    if (Field.isDateField(item)) {
      return (
        <>
          <div>{item.title?.[currentLanguage]}</div>
          <DateField
            {...field}
            fullWidth
            label={item.desc?.[currentLanguage]}
            helperText={fieldState.error?.message}
            error={!!fieldState.error}
          />
        </>
      );
    } else throw new Error("Date incorrectly configured");
  },
  DROP_DOWN: ({ item }) => {
    const { control } = useFormContext();
    const { field, fieldState } = useController({
      control,
      name: item.field,
    });
    const { i18n } = useTranslation();
    const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
      i18n,
    ]);

    if (Field.isSelectField(item)) {
      return (
        <>
          <div>{item.title?.[currentLanguage]}</div>
          <Select
            {...field}
            fullWidth
            label={item.desc?.[currentLanguage]}
            error={!!fieldState.error}
            helperText={fieldState.error?.message}
            options={
              item.typeDetails.availableOptions?.[currentLanguage]
                ? item.typeDetails.availableOptions[currentLanguage].map(
                    (value) => ({
                      title: value,
                      value,
                    })
                  )
                : []
            }
          />
        </>
      );
    } else throw new Error("Select incorrectly configured");
  },
  NUMBER: ({ item }) => {
    const { control } = useFormContext();
    const { field, fieldState } = useController({
      control,
      name: item.field,
    });
    const { i18n } = useTranslation();
    const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
      i18n,
    ]);

    if (Field.isNumberField(item)) {
      return (
        <>
          <div>{item.title?.[currentLanguage]}</div>
          <NumberField
            {...field}
            fullWidth
            label={item.desc?.[currentLanguage]}
            helperText={fieldState.error?.message}
            error={!!fieldState.error}
          />
        </>
      );
    } else throw new Error("Number incorrectly configured");
  },
  RADIO: ({ item }) => {
    const { control } = useFormContext();
    const { field } = useController({
      control,
      name: item.field,
    });
    const { i18n } = useTranslation();
    const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
      i18n,
    ]);

    if (Field.isRadioField(item) && item.typeDetails) {
      return (
        <>
          <div>{item.title?.[currentLanguage]}</div>
          <RadioGroup<any>
            options={
              item.typeDetails.availableOptions?.[currentLanguage]
                ? item.typeDetails.availableOptions[currentLanguage].map(
                    (val) => ({
                      id: val,
                      label: val,
                    })
                  )
                : []
            }
            {...field}
          />
        </>
      );
    } else throw new Error("Radio incorrectly configured");
  },
  TEXT: ({ item }) => {
    const { control } = useFormContext();
    const { field, fieldState } = useController({
      control,
      name: item.field,
    });
    const { i18n } = useTranslation();
    const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
      i18n,
    ]);

    if (Field.isTextField(item)) {
      return (
        <>
          <div>{item.title?.[currentLanguage]}</div>
          <TextField
            {...field}
            fullWidth
            label={item.desc?.[currentLanguage]}
            helperText={fieldState.error?.message}
            error={!!fieldState.error}
          />
        </>
      );
    } else throw new Error("Date incorrectly configured");
  },
  FILE_UPLOAD: ({ item }) => {
    const { control } = useFormContext();
    const { field, fieldState } = useController({
      control,
      name: item.field,
    });
    const { i18n } = useTranslation();
    const currentLanguage = useMemo(() => i18n.language.replaceAll("-", "_"), [
      i18n,
    ]);

    if (Field.isFileField(item)) {
      return (
        <>
          <div>{item.title?.[currentLanguage]}</div>
          <FileField
            {...field}
            label={item.desc?.[currentLanguage]}
            helperText={fieldState.error?.message}
            error={!!fieldState.error}
          />
        </>
      );
    } else throw new Error("Custom File incorrectly configured");
  },
};

export function useBasicFieldsValidation() {
  const { i18n } = useTranslation();
  const currentLanguage = i18n.language.replaceAll("-", "_");

  return useCallback<
    (
      f: Exclude<Field.Field<string>, Field.DefaultField>
    ) => { schema: BaseSchema<any>; skipOptionalCheck?: boolean }
  >(
    (f) => {
      if (f.type === "MULTI_CHECKBOX") {
        return { schema: array().label(f.title[currentLanguage]) };
      } else if (f.type === "MULTI_SELECT") {
        return { schema: array().label(f.title[currentLanguage]) };
      } else if (f.type === "SWITCH") {
        return {
          schema: boolean().label(f.title[currentLanguage]),
          skipOptionalCheck: true,
        };
      } else if (f.type === "DATE") {
        let temp = date().label(f.title[currentLanguage]);
        if (f.typeDetails) {
          return {
            schema: temp
              .max(f.typeDetails.between.end)
              .min(f.typeDetails.between.start),
          };
        }
        return { schema: temp };
      } else if (f.type === "DROP_DOWN") {
        let temp = mixed().label(f.title[currentLanguage]);
        if (f.typeDetails) {
          return {
            schema: (temp = temp.oneOf(
              f.typeDetails.availableOptions?.[currentLanguage]
            )),
          };
        }
        return { schema: temp };
      } else if (f.type === "NUMBER") {
        let temp = number().label(f.title[currentLanguage]);
        if (f.typeDetails) {
          return { schema: temp.max(f.typeDetails.max).min(f.typeDetails.min) };
        }
        return { schema: temp };
      } else if (f.type === "RADIO") {
        let temp = mixed().label(f.title[currentLanguage]);
        if (f.typeDetails) {
          return {
            schema: (temp = temp.oneOf(
              f.typeDetails.availableOptions?.[currentLanguage]
            )),
          };
        }
        return { schema: temp };
      } else if (f.type === "TEXT") {
        let temp = string().label(f.title[currentLanguage]);
        if (f.typeDetails) {
          temp = temp
            .min(f.typeDetails.minCharacters)
            .max(f.typeDetails.maxCharacters);
          if (f.typeDetails.pattern) {
            return { schema: temp.matches(new RegExp(f.typeDetails.pattern)) };
          }
        }
        return { schema: temp };
      }
      return { schema: mixed() };
    },
    [currentLanguage]
  );
}
